import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type components } from "@njbsoft/types";
import { ApiError, permitKeys } from "@njbsoft/api-client";
import { useAppStore } from "@/store";
import { useApiClient } from "@/lib/auth";
import { useFacilities } from "@/hooks/use-facilities";
import { useToast } from "@/hooks/use-toast";
import { PermitForm } from "@/components/permits/permit-form";
import { PageHeader, Breadcrumbs } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/permits/new")({
  component: PermitCreatePage,
});

function PermitCreatePage() {
  const { t } = useTranslation("permits");
  const navigate = useNavigate();
  const toast = useToast();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const orgId = useAppStore((s) => s.activeOrgId);
  const [facilitySearch, setFacilitySearch] = useState("");

  const { data: facilitiesData, isFetching: facilitiesFetching } = useFacilities(orgId, {
    limit: 20,
    status: "active",
    search: facilitySearch || undefined,
  });
  const facilities = facilitiesData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: async ({
      facilityId,
      body,
    }: {
      facilityId: string;
      body: components["schemas"]["PermitCreate"];
    }) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await api.POST(
        "/orgs/{orgId}/facilities/{facilityId}/permits",
        {
          params: { path: { orgId, facilityId } },
          body,
        },
      );
      if (error) throw new ApiError(response.status, error.error ?? "Request failed");
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: permitKeys.list(orgId ?? "") });
    },
  });

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs
        items={[{ label: t("title"), to: "/permits" }, { label: t("form.createTitle") }]}
      />
      <PageHeader title={t("form.createTitle")} />

      <div className="mt-6">
        <PermitForm
          mode="create"
          facilities={facilities}
          onFacilitySearch={setFacilitySearch}
          isFacilitySearching={facilitiesFetching}
          isPending={createMutation.isPending}
          onSubmit={(values) => {
            createMutation.mutate(
              {
                facilityId: values.facility_id,
                body: {
                  permit_number: values.permit_number,
                  permit_type: values.permit_type,
                  status: values.status || undefined,
                  issued_date: values.issued_date || undefined,
                  effective_date: values.effective_date || undefined,
                  expiration_date: values.expiration_date || undefined,
                  issuing_agency: values.issuing_agency || undefined,
                  description: values.description || undefined,
                },
              },
              {
                onSuccess: (data) => {
                  toast.success(t("toast.createSuccess"));
                  void navigate({
                    to: "/permits/$permitId",
                    params: { permitId: data.id },
                  });
                },
                onError: () => {
                  toast.error(t("toast.createError"));
                },
              },
            );
          }}
        />
      </div>
    </div>
  );
}
